import { Label, Notification } from '@schibsted-svp/react-ui';
import classnames from 'classnames/bind';
import CropImageDialog from 'components/video/CropImageDialog';
import PosterPreview from 'components/video/VideoPoster/PosterPreview';
import PosterUploader from 'components/video/VideoPoster/PosterUploader';
import validate from 'components/video/VideoPoster/PosterUploader.validator';
import config from 'config';
import useToggle from 'hooks/useToggle';
import { reportMessage } from 'lib/error';
import type { ChangeEvent } from 'react';
import { type Control, type FieldValues, Path, useController } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { revokeVideoPosterPreview } from 'store/video/upload/actions';
import { getPosterPreviewUrl, isVideoPosterUploading } from 'store/video/upload/selectors';
import type { ThemeImageType } from '../../../screens/NewsroomScreen/PlaylistsScreen/types';
import type { ImageType } from '../types';
import css from './Poster.module.scss';
import { useUploadedPosterChange } from './hooks/poster';

const cln = classnames.bind(css);

const defaultMinimumSize = config.images.poster.upload.minimumSize;
const defaultMaximumSize = config.images.poster.upload.maximumSize;

const typeToLabel = ({ type }) => (type === 'front' ? 'additional' : type);

interface PosterManagerProps<TFieldValues extends FieldValues> {
    id: number | string;
    provider: string;
    type: ImageType | ThemeImageType;
    control: Control<TFieldValues>;
    ready: boolean;
    onVideoPosterUpload: (file: File, type: ImageType | ThemeImageType) => void;
    label?: string;
    maximumSize?: [number, number];
    minimumSize?: [number, number];
    enableCrop?: boolean;
    alwaysAllowDelete?: boolean;
    posterContainerClassName?: string;
}

export function PosterManager<TFieldValues extends FieldValues>({
    id,
    provider,
    type,
    control,
    ready,
    label,
    minimumSize,
    maximumSize,
    enableCrop = false,
    alwaysAllowDelete = false,
    posterContainerClassName,
    onVideoPosterUpload,
}: PosterManagerProps<TFieldValues>) {
    const {
        field: { value, onChange },
        fieldState: { isDirty },
        formState: {
            defaultValues: { images },
        },
    } = useController({
        name: `images.${type}` as Path<TFieldValues>,
        control,
    });
    const dispatch = useDispatch();

    const defaultValue = images[type];

    const poster = useSelector((state) => getPosterPreviewUrl(state, { provider, id, type })) || value;
    const uploading = useSelector((state) => isVideoPosterUploading(state, { provider, id, type }));

    useUploadedPosterChange({ provider, id, type, onChange });

    const [openedCropDialog, toggleOpenedCropDialog] = useToggle();
    const [minWidth, minHeight] = minimumSize ?? defaultMinimumSize;
    const [maxWidth, maxHeight] = maximumSize ?? defaultMaximumSize;

    const handleDeleteUploadedImage = () => {
        onChange(alwaysAllowDelete ? null : defaultValue);
        dispatch(revokeVideoPosterPreview(provider, id, type));
    };

    const handlePosterChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files ?? []);
        try {
            const result = await validate(files, { minWidth, minHeight, maxWidth, maxHeight });
            if (result.isValid) {
                onVideoPosterUpload(result.file, type);
            } else {
                Notification.notify.error(result.message);
            }
        } catch (error) {
            const message =
                'Something went wrong during upload. Please contact us on Slack or on mail:svp@schibsted.com';

            reportMessage({
                message,
                error,
            });
            Notification.notify.error(message);
        }
    };

    return (
        <div className={css.posters}>
            <div className={cln('posterContainer', posterContainerClassName)}>
                <Label className={css.label}>{label ?? `${typeToLabel({ type })} image`}</Label>
                <div className={css.poster}>
                    <PosterPreview
                        loading={uploading}
                        src={poster}
                        enableCrop={enableCrop}
                        toggleCropImageDialog={toggleOpenedCropDialog}
                        clearUploadedImage={
                            (isDirty || alwaysAllowDelete) && poster ? handleDeleteUploadedImage : undefined
                        }
                        handlePosterChange={handlePosterChange}
                        onVideoPosterUpload={onVideoPosterUpload}
                        minimumSize={[minWidth, minHeight]}
                        maximumSize={[maxWidth, maxHeight]}
                        type={type}
                    />
                </div>
                <div className={css.uploader}>
                    <PosterUploader
                        disabled={uploading || !ready}
                        handlePosterChange={handlePosterChange}
                        isEmpty={poster === null}
                        minimumSize={[minWidth, minHeight]}
                        maximumSize={[maxWidth, maxHeight]}
                    />
                </div>
                {enableCrop && poster && (
                    <CropImageDialog
                        opened={openedCropDialog}
                        src={poster}
                        toggle={toggleOpenedCropDialog}
                        onVideoPosterUpload={onVideoPosterUpload}
                        type={type}
                    />
                )}
            </div>
        </div>
    );
}
